import {
    Stack,
    Typography,
    Card,
    CardContent,
    Box,
    Chip,
    Grid,
    Avatar,
    useTheme,
    SvgIconProps,
    Tooltip, CardHeader, Divider
} from "@mui/material"
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import {ComponentType, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {numberFormatter} from "../../../utils/formatters/numberFormatter";
import {EstadoColorMap} from "../../solicitations/SolicitationPreview";
import {Solicitation, SolicitationFields} from "../../../types/solicitation/solicitationData";
import {
    EntityWithIdAndDescription,
    EntityWithIdAndDescriptionFields,
    EntityWithIdFields
} from "../../../types/baseEntities";
import {
    SolicitationFinancePreferences,
    SolicitationFinancePreferencesFields
} from "../../../types/financing/financingData";
import {dateFormatter} from "../../../utils/formatters";
import {HttpCacheOperation} from "../../../http/cache/httpCacheOperation";
import {OperationReasonDestination, OperationReasonDestinationFields} from "../../../types/operationData";
import {BackButton} from "../../../components/buttons/Buttons";

// const Entrada = ({ label, info }: { label?: string, info: string }) => {

//     return (
//         <Stack direction='row' display='flex' justifyContent='space-between'>
//             <Typography variant='subtitle1'>{label && label + ':'}</Typography>
//             <Typography variant='subtitle2'>{info}</Typography>
//         </Stack>
//     )
// }

interface TabsSolicitationProps {
    solicitud: Solicitation,
    preferencias: SolicitationFinancePreferences
}

interface DetailBlockProps {
    label: string
    value: string,
    category?: string
    Icon: ComponentType<SvgIconProps>,
    center?: boolean
}

const DetailBlock = (props: DetailBlockProps) => {

    const { label, value, category, Icon } = props

    const theme = useTheme();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }} justifyContent={props.center ? 'center' : 'flex-start'}>
            <Avatar variant='rounded' sx={{ bgcolor: theme.palette.primary.main, mr: 4 }}>
                <Icon sx={{ color: 'white' }} />
            </Avatar>
            <Stack>
                <Stack direction={'row'} alignItems={'center'}>
                    <Typography variant='subtitle1'>{value}</Typography>
                    {category && <Typography variant='subtitle1' fontWeight={600} pl={1}>{`(${category})`}</Typography>}
                </Stack>
                <Typography variant='subtitle2'>{label}</Typography>
            </Stack>
        </Box>
    )
}

const DetailBlocksSection = ({ solicitud, preferencias }: { solicitud: Solicitation, preferencias: SolicitationFinancePreferences }) => {
    

    return (
        <Grid container spacing={4}>
            <Grid item xs={6} md={2.5}>
                {
                    preferencias &&
                    <DetailBlock
                        label='Monto solicitado'
                        value={numberFormatter.toStringWithAmount(preferencias[SolicitationFinancePreferencesFields.Amount], '$')}
                        Icon={AttachMoneyIcon}
                    />
                }
            </Grid>
            <Grid item xs={6} md={2.5}>
                {
                    preferencias &&
                    <DetailBlock
                        label='Plazo'
                        value={`${preferencias[SolicitationFinancePreferencesFields.Term] ? `${preferencias[SolicitationFinancePreferencesFields.Term]} meses` : '-'}`}
                        Icon={CalendarMonthIcon}
                    />
                }
            </Grid>
            <Grid item xs={6} md={2.5}>
                {
                    preferencias &&
                    <DetailBlock
                        label='Banco preferido'
                        value={solicitud[SolicitationFields.BankDesc] ?? '-'}
                        Icon={AccountBalanceIcon}
                    />
                }
            </Grid>
            <Grid item xs={6} md={2.25}>
                <DetailBlock
                    label='Fecha Alta'
                    value={dateFormatter.toShortDate(solicitud[SolicitationFields.BeginDate])}
                    Icon={DoneIcon}
                />
            </Grid>
            <Grid item xs={6} md={2.25}>
                <DetailBlock
                    label='Fecha Envío'
                    value={dateFormatter.toShortDate(solicitud[SolicitationFields.SentDate])}
                    Icon={DoneAllIcon}
                />
            </Grid>
        </Grid>
    )
}

const SummaryPanel = (props: TabsSolicitationProps) => {
    const navigate = useNavigate()
    const [reasonDestination, setReasonDestination] = useState<string>('')
    const [destination, setDestination] = useState<string>('')

    useEffect(() => {
        HttpCacheOperation.getDestinations().then((dests) => {
            HttpCacheOperation.getReasonsDestination().then((reasons) => {
                const reason: OperationReasonDestination | undefined = reasons.find((reason) => reason[EntityWithIdFields.Id] === props.preferencias[SolicitationFinancePreferencesFields.DestinationReasonCode])
                if (reason) {
                    const dest: EntityWithIdAndDescription | undefined = dests.find((des) => des[EntityWithIdFields.Id] === reason[OperationReasonDestinationFields.OperationDestinationCode])
                    if (dest) {
                        setDestination(dest[EntityWithIdAndDescriptionFields.Description])
                    }
                    setReasonDestination(reason[EntityWithIdAndDescriptionFields.Description])
                }
            })
        })

    }, [])
    
    return (
        <Card>
            <CardHeader title={
                <Stack spacing={1}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        <Stack direction={'row'} alignItems={'center'} spacing={2}>
                            <Typography variant='h6'>
                                Solicitud #{props.solicitud[EntityWithIdFields.Id]}
                            </Typography>   
                            <Chip
                                label={props.solicitud[SolicitationFields.SolicitationStateDesc]}
                                color={EstadoColorMap(props.solicitud[SolicitationFields.SolicitationStateCode])}
                                size='small'
                                variant='filled'
                            />
                        </Stack>
                        <BackButton onClick={() => navigate('/solicitudes')}>Inicio</BackButton>
                    </Stack>
                    <Stack direction={'row'} alignItems={'center'} spacing={2}>
                        <Typography fontSize={22} fontWeight={600}>
                            {props.solicitud[SolicitationFields.TitularBusinessName]}
                        </Typography>
                        <Tooltip title={'CUIT'}>
                            <Chip label={numberFormatter.toCuitString(props.solicitud[SolicitationFields.TitularCUIT])}
                                  size={'small'}
                                  variant={'outlined'}
                                  color={'info'}
                            />
                        </Tooltip>
                    </Stack>
                        {
                            reasonDestination !== '' && destination !== '' &&
                            <DetailBlock label='Destino' 
                                         value={reasonDestination} 
                                         category={destination} 
                                         Icon={AgricultureIcon}
                            />
                        }
                </Stack>
            }
            />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}><Divider/></Grid>
                        <Grid container item md={12} mt={4}>
                            <DetailBlocksSection solicitud={props.solicitud} preferencias={props.preferencias}/>
                        </Grid>
                    </Grid>
                    
                    {
                        
                        /* 
                        
                        <Box pt={2}>
                        <Entrada label='Motivo' info={destino} />
                        <Entrada label='Monto solicitado' info={`$ ${montoSolicitado}`} />
                        <Entrada label='Plazo de la operación' info={'18 meses'} />
                        <Entrada label='Banco de preferencia' info={'No tengo preferencia'} />
                        <Entrada label='Fecha alta' info={fechaAlta} />
                        <Entrada label='Fecha envío' info={fechaEnvio || ''} />
                    </Box> 
                    */
                    }
                    
                </CardContent>
            
                {
                    
                    /*
                    
                    <CardContent>
                        <Typography variant='h6'>Historia</Typography>
                        <Divider />
                        <Box pt={2}>
                            <SolicitudHistoria historia={historia} />
                        </Box>
                    </CardContent>
                    
                     */
                    
                }
                
        </Card >
    )
}

export default SummaryPanel